// ==================== SMS TEMPLATES SERVICE ====================

import { apiService } from './api';

// ==================== TYPES ====================

export type SmsTemplateCategory = 
  | 'MARKETING'
  | 'TRANSACTIONAL'
  | 'NOTIFICATION'
  | 'AUTHENTICATION'
  | 'CUSTOMER_SERVICE';

export interface SmsTemplateVariable {
  variable: string;
  description: string;
  example?: string;
}

export interface SmsTemplate {
  id: string;
  name: string;
  description: string | null;
  category: SmsTemplateCategory;
  content: string;
  variables: SmsTemplateVariable[] | null;
  characterCount: number;
  segmentCount: number;
  isActive: boolean;
  usageCount: number;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
  organizationId: string;
}

export interface CreateSmsTemplateData {
  name: string;
  description?: string;
  category: SmsTemplateCategory;
  content: string;
}

export interface UpdateSmsTemplateData extends Partial<CreateSmsTemplateData> {
  isActive?: boolean;
}

export interface SmsTemplateStats {
  total: number;
  active: number;
  inactive: number;
  byCategory: Record<SmsTemplateCategory, number>;
  recentlyUsed: Array<{
    id: string;
    name: string;
    lastUsedAt: string;
    usageCount: number;
  }>;
}

export interface SmsTemplatePreview {
  preview: string;
  characterCount: number;
  segmentCount: number;
}

interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ==================== SERVICE ====================

class SmsTemplatesService {
  private basePath = '/customer/sms-templates';

  /**
   * Create a new SMS template
   */
  async create(data: CreateSmsTemplateData): Promise<SmsTemplate> {
    const response = await apiService.post<SmsTemplate>(this.basePath, data);
    if (!response.success) {
      throw new Error(response.message || 'Failed to create SMS template');
    }
    // Response is { success, message, data: SmsTemplate }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response as any).data;
  }

  /**
   * Get all SMS templates with optional filters
   */
  async getAll(options?: {
    search?: string;
    category?: SmsTemplateCategory;
    isActive?: boolean;
    page?: number;
    limit?: number;
  }): Promise<PaginatedResponse<SmsTemplate>> {
    const params = new URLSearchParams();
    
    if (options?.search) params.append('search', options.search);
    if (options?.category) params.append('category', options.category);
    if (options?.isActive !== undefined) params.append('isActive', String(options.isActive));
    if (options?.page) params.append('page', String(options.page));
    if (options?.limit) params.append('limit', String(options.limit));

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;

    // apiService.get returns the response body directly: { success, data, pagination }
    const response = await apiService.get<SmsTemplate[]>(url);
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch SMS templates');
    }
    
    // Response structure: { success: true, data: [...], pagination: {...} }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fullResponse = response as any;
    
    return {
      success: true,
      data: fullResponse.data || [],
      pagination: fullResponse.pagination || { page: 1, limit: 12, total: 0, totalPages: 0 },
    };
  }

  /**
   * Get a single SMS template by ID
   */
  async getById(id: string): Promise<SmsTemplate> {
    const response = await apiService.get<SmsTemplate>(`${this.basePath}/${id}`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch SMS template');
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response as any).data;
  }

  /**
   * Update an SMS template
   */
  async update(id: string, data: UpdateSmsTemplateData): Promise<SmsTemplate> {
    const response = await apiService.put<SmsTemplate>(`${this.basePath}/${id}`, data);
    if (!response.success) {
      throw new Error(response.message || 'Failed to update SMS template');
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response as any).data;
  }

  /**
   * Delete an SMS template
   */
  async delete(id: string): Promise<void> {
    const response = await apiService.delete(`${this.basePath}/${id}`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete SMS template');
    }
  }

  /**
   * Duplicate an SMS template
   */
  async duplicate(id: string): Promise<SmsTemplate> {
    const response = await apiService.post<SmsTemplate>(`${this.basePath}/${id}/duplicate`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to duplicate SMS template');
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response as any).data;
  }

  /**
   * Get template statistics
   */
  async getStats(): Promise<SmsTemplateStats> {
    const response = await apiService.get<SmsTemplateStats>(`${this.basePath}/stats`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch SMS template stats');
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response as any).data;
  }

  /**
   * Get available template variables
   */
  async getVariables(): Promise<SmsTemplateVariable[]> {
    const response = await apiService.get<SmsTemplateVariable[]>(`${this.basePath}/variables`);
    if (!response.success) {
      throw new Error(response.message || 'Failed to fetch template variables');
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response as any).data;
  }

  /**
   * Preview a template with sample data
   */
  async preview(content: string, sampleData?: Record<string, string>): Promise<SmsTemplatePreview> {
    const response = await apiService.post<SmsTemplatePreview>(`${this.basePath}/preview`, {
      content,
      sampleData,
    });
    if (!response.success) {
      throw new Error(response.message || 'Failed to preview template');
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (response as any).data;
  }

  /**
   * Calculate SMS segments for given content
   */
  calculateSegments(content: string): { characterCount: number; segmentCount: number } {
    const characterCount = content.length;
    const hasUnicode = /[^\x00-\x7F]/.test(content);
    
    let segmentCount: number;
    if (hasUnicode) {
      if (characterCount <= 70) {
        segmentCount = 1;
      } else {
        segmentCount = Math.ceil(characterCount / 67);
      }
    } else {
      if (characterCount <= 160) {
        segmentCount = 1;
      } else {
        segmentCount = Math.ceil(characterCount / 153);
      }
    }

    return { characterCount, segmentCount };
  }

  /**
   * Extract variables from content
   */
  extractVariables(content: string): string[] {
    const variablePattern = /\{\{(\w+)\}\}/g;
    const matches = content.matchAll(variablePattern);
    const variables = new Set<string>();

    for (const match of matches) {
      variables.add(match[1]);
    }

    return Array.from(variables);
  }
}

export const smsTemplatesService = new SmsTemplatesService();
